import { z } from "zod";

import { getNormalEnum } from "./pseudoEnums.js";

export const ComplianceTypeLiteral = z.enum(["eu", "hipaa", "pci", "none"]);

export type ComplianceType = z.infer<typeof ComplianceTypeLiteral>;
export const ComplianceType = getNormalEnum(ComplianceTypeLiteral);

export type ProductionEnvironment =
  | "prod"
  | "staging"
  | "develop"
  | "local"
  | "self-hosted"
  | "single-tenant"
  | "unknown";

export const GLOBAL_ORG_SHARING_ENVIRONMENTS: ProductionEnvironment[] = [
  "local",
  "self-hosted",
  "single-tenant",
  "develop",
  "unknown",
];

// NOTE: Ported to Java in data-service/src/main/java/com/hex/launchdarkly/EnvironmentUtils.java
// (getProductionEnvironment) so the data-service resolves the same LaunchDarkly environment as
// server/sidecar. Keep the two in sync — any change to this logic must be mirrored there.
export const getProductionEnvironment = (
  url: string,
): ProductionEnvironment => {
  let hostname;
  if (url.match(/https?:\/\//)) {
    try {
      hostname = new URL(url).hostname;
    } catch (e) {
      console.error(e);
      throw new Error(
        `Invalid hostname specified for production environment: ${hostname}`,
      );
    }
  } else {
    hostname = url;
  }

  const [initialSubDomain, ...rootDomains] = hostname.split(".");
  const rootDomain = rootDomains.join(".");
  if (!["hex.site", "hex.tech"].includes(rootDomain)) {
    if ("snowflakecomputing.app" === rootDomain) {
      return "prod";
    }
    if (initialSubDomain?.match(/localhost(:\d+)?/)) {
      return "local";
    }
  }
  const subDomain =
    initialSubDomain?.includes("develop") || initialSubDomain?.includes("dev-")
      ? "develop"
      : initialSubDomain;

  // TODO(FOUND-179): Remove this manual check once we have consolidated RC and MT via Terraform.
  // We need this check for now to ensure that rc/hex and rc/hex-testing both use the same feature flag
  // configuration that they had on the MT stack.
  switch (subDomain) {
    case "app":
    case "eu":
    case "hc":
    case "rc":
      return "prod";
    case "staging-1":
      return "staging";
    case "develop":
      return "develop";
    default:
      return "single-tenant";
  }
};

export function shouldAllowDangerouslyUnsafeLogging({
  environment,
  nodeEnv,
}: {
  environment: ProductionEnvironment;
  nodeEnv?: string;
}): boolean {
  return (
    nodeEnv === "development" ||
    ["local", "develop", "staging"].includes(environment)
  );
}
